import { AsyncPipe, Location } from '@angular/common'
import { Component, computed, effect, inject, OnDestroy, OnInit } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { MatDialogRef } from '@angular/material/dialog'
import { RouterOutlet } from '@angular/router'
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker'
import { getBrowserLang, TranslocoService } from '@jsverse/transloco'
import { Store } from '@ngrx/store'
import { AngularQueryDevtools } from '@tanstack/angular-query-devtools-experimental'
import { injectQueryClient } from '@tanstack/angular-query-experimental'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { Observable, Subscription } from 'rxjs'
import { delay, distinctUntilChanged, filter, map } from 'rxjs/operators'
import { ChannelFetchedComponent } from 'src/app/common/components/channel-fetched/channel-fetched.component'
import { UpdateDialogComponent } from 'src/app/common/components/update-dialog/update-dialog.component'

import { UserService } from '~core/services'
import { DialogService } from '~core/services/ui/dialog.service'
import { ScreenService, ScreenSize } from '~core/services/ui/screen.service'
import { WebsocketService, WebsocketServiceLegacy } from '~core/services/websocket.service'
import { selectIsLoading } from '~core/store/core.selectors'
import { setScreenSize } from '~core/store/ui/ui.actions'
import { selectIsScreenSizeGte } from '~core/store/ui/ui.selectors'
import logger from '~core/utils/logger'
import { StartupView } from '~core/views/startup/startup.view'
import { environment } from '~env'
import { TiktokUserWsMessageType, UserConnectionWsMessage } from '~features/tiktok-user/types'
import { User } from '~features/user/models/user.model'

import { LoaderComponent } from './common/components/loader/loader.component'

dayjs.extend(isBetween)

@Component({
  selector: 'sb-root',
  template: `
    @if (startupCompleted()) {
      @if (isLoading$ | async) {
        <sb-loader overlay />
      }
      <router-outlet />
    } @else {
      <sb-startup-view />
    }
    @if (!environment.production) {
      <angular-query-devtools />
    }
  `,
  standalone: true,
  imports: [LoaderComponent, RouterOutlet, AsyncPipe, AngularQueryDevtools, StartupView],
})
export class AppComponent implements OnInit, OnDestroy {
  #ws = inject(WebsocketService)
  isLoading$: Observable<boolean>
  user: User
  startupCompleted = computed(() => this.userService.startupCompleted())
  #project = toSignal(this.userService.currentProject$)
  protected environment = environment
  #queryClient = injectQueryClient()

  private channelFetchedEvents = [
    'FacebookPageFetched',
    'InstagramAccountFetched',
    'FacebookAdAccountFetched',
    'LinkedinOrganizationFetched',
    'TiktokUserFetched',
  ]
  private subscriptions: Subscription[] = []

  constructor(
    private store: Store,
    private location: Location,
    private swUpdate: SwUpdate,
    private userService: UserService,
    private dialogService: DialogService,
    private websocketService: WebsocketServiceLegacy,
    private translateService: TranslocoService,
    private screenService: ScreenService,
  ) {
    effect(
      () => {
        const wsMsg = this.#ws.getFilteredMessage<UserConnectionWsMessage>(TiktokUserWsMessageType)
        if (wsMsg()?.payload?.progress === 100) {
          this.userService.onAddTiktokUser(wsMsg()?.payload?.tiktokUser)
          this.#queryClient.invalidateQueries({ queryKey: [wsMsg()?.payload?.tiktokUser.projectId, 'tiktok'] })
        }
      },
      { allowSignalWrites: true },
    )
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe())
  }

  ngOnInit(): void {
    this.handleScreenSize()
    const locale = this.getGuestLanguage()
    this.isLoading$ = this.store.select(selectIsLoading).pipe(delay(0), distinctUntilChanged())
    this.translateService.setActiveLang(locale)
    if (!this.isPublicUrl()) {
      this.userService.populate()
      this.listenToChannelFetch()
    } else {
      this.userService.startupCompleted.set(true)
    }
    this.handleUpdates()
  }

  private changeCustomerlyVisibility(visible: boolean) {
    // Check if customerly exists
    if (typeof (<any>window).customerly !== 'undefined') {
      ;(<any>window).customerly.update({ visible })
    }
  }

  private getGuestLanguage() {
    let locale = getBrowserLang()
    if (environment.availableLanguages.indexOf(locale) === -1) {
      locale = 'en'
    }

    return locale
  }

  private handleScreenSize() {
    this.subscriptions.push(
      this.screenService.size.subscribe((payload) => {
        this.store.dispatch(setScreenSize({ payload }))
      }),
      this.store.select(selectIsScreenSizeGte(ScreenSize.Medium)).subscribe((isGteMd) => {
        this.changeCustomerlyVisibility(isGteMd ? true : false)
      }),
    )
  }

  private handleUpdates() {
    if (!this.swUpdate.isEnabled) {
      logger.info('SwUpdate not enabled')
    } else {
      this.swUpdate.versionUpdates
        .pipe(
          filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
          map((evt) => ({
            type: 'UPDATE_AVAILABLE',
            current: evt.currentVersion,
            available: evt.latestVersion,
          })),
        )
        .subscribe((event) => {
          if (environment.forceUpdate) {
            this.swUpdate.activateUpdate().then(() => document.location.reload())
          } else {
            logger.info('Update available: current version is', event.current, 'available version is', event.available)
            this.dialogService.open(UpdateDialogComponent, { width: '450px' })
          }
        })
    }
  }

  private isPublicUrl() {
    return this.location.path().startsWith('/public/')
  }

  private listenToChannelFetch() {
    let dialog: MatDialogRef<ChannelFetchedComponent>
    this.channelFetchedEvents.forEach((event) =>
      this.subscriptions.push(
        this.websocketService.listen(event).subscribe((channel) => {
          if (!dialog) {
            dialog = this.dialogService.open(ChannelFetchedComponent, { data: { channels: [channel] } })
            const sub = dialog.afterClosed().subscribe(() => {
              dialog = null
              sub.unsubscribe()
            })
          } else {
            dialog.componentInstance.channels.push(channel)
          }
          this.userService.onRefreshChannel(channel)
        }),
      ),
    )
  }
}
